import { useSessionFormState } from '@hooks/useSessionFormState';
import { Checkbox, CheckboxChangeEvent, Form, Tooltip } from 'antd';
import { useNotification } from '../../../../contexts/NotificationContext/NotificationContext';
import React, { type ComponentProps } from 'react';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { useTranslation } from 'react-i18next';
import useCase from '@hooks/useCase';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { useMutation } from '@tanstack/react-query';
import * as actions from '@actions';
import useUser from '@vl-core/hooks/useUser';
import { useDispatch } from 'react-redux';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';

export const useValidateDocmanDiscussionFormField = () => {
  const { DOCMAN } = useRuntimeConfig();
  const [docmanDiscussed] = useSessionFormState('docmanDiscussed');
  const [isDPACheck] = useSessionFormState('isDPACheck');
  const {
    appointment: { case_id }
  } = useSessionAppointment();
  const [caseDetails] = useCase(case_id);

  const shouldRequireDocmanDiscussion = DOCMAN && caseDetails?.docman_active && !caseDetails?.docman_consent;

  return () => {
    if (!isDPACheck) {
      return true;
    }

    if (!shouldRequireDocmanDiscussion) {
      return true;
    }

    return docmanDiscussed;
  };
};

const DocmanDiscussionFormItem = (props: ComponentProps<typeof SessionFormItem>) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { errorNotification } = useNotification();
  const user = useUser();
  const {
    appointment: { appointment_completed, case_id }
  } = useSessionAppointment();
  const [caseDetails, invalidateCaseDetails] = useCase(case_id);
  const [docmanDiscussed, setDocmanDiscussed] = useSessionFormState(
    'docmanDiscussed',
    // @ts-ignore
    !caseDetails?.docman_clin_consent_required
  );

  const { mutate: mutateDocmanHasDiscussed } = useMutation({
    mutationFn: () => {
      if (!caseDetails) throw new Error('Missing case details');

      return dispatch(
        actions.updatePatientDocmanConsent({
          caseId: caseDetails.case_id,
          clinicianId: user.user_guid,
          docmanConsent: caseDetails.docman_consent ? 1 : 0
        })
      );
    },
    onSuccess: (data) => {
      invalidateCaseDetails();
    },
    onError: (error) => {
      errorNotification({
        message: 'Error',
        description: t('Error while confirming discussion')
      });
      setDocmanDiscussed(false);
      console.error('Error while update patient consent request: ', error);
    }
  });

  const onChange = (event: CheckboxChangeEvent) => {
    setDocmanDiscussed(event.target.checked);
    mutateDocmanHasDiscussed();
  };

  return (
    <>
      <Form.Item
        required
        colon={false}
        name="docmanDiscussed"
        label={
          <strong>
            {t('I confirm that I have discussed document sharing with the patient during this appointment')}:
          </strong>
        }
        {...props}
      >
        <Checkbox
          data-testid="docman-discussed-checkbox"
          disabled={appointment_completed || docmanDiscussed}
          checked={docmanDiscussed}
          onChange={onChange}
        />
      </Form.Item>

      <Tooltip
        title={t(
          'Professional regulators advise that a patient’s care information is shared with their GP; where a patient declines, the reasons for this should be explored.'
        )}
        trigger={['hover', 'focus']}
      >
        <UnstyledButton style={{ position: 'relative', right: 10 }} data-testid=''>
          <QuestionCircleOutlined aria-hidden />
        </UnstyledButton>
      </Tooltip>
    </>
  );
};

export default DocmanDiscussionFormItem;
